/**
 * Lists video interview recordings for a candidate, with embedded HTML5
 * playback and the Claude-generated summary alongside.
 *
 * Data sources:
 * - `GET /api/candidates/{id}/interviews` — list of interviews + per-record
 *   `transcript` and `ai_summary` already merged.
 * - `GET /api/video/interviews/{id}/recordings` — presigned playable URLs
 *   (fetched lazily when a row is expanded).
 */
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  Loader2,
  Video as VideoIcon,
  PlayCircle,
  Sparkles,
  FileText,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Link2,
  Copy,
  Send,
  ChevronDown,
  ChevronUp,
  Zap,
  Brain,
  HelpCircle,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const SUMMARY_RECO_STYLES = {
  avancar: { label: "Avançar", icon: CheckCircle2, klass: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  considerar: { label: "Considerar", icon: AlertCircle, klass: "bg-amber-50 text-amber-700 border-amber-200" },
  reprovar: { label: "Reprovar", icon: XCircle, klass: "bg-rose-50 text-rose-700 border-rose-200" },
};

// ── Highlight categories — colour + icon per slug ────────────────────
const HIGHLIGHT_CATEGORIES = {
  ponto_forte: {
    label: "Ponto forte",
    icon: CheckCircle2,
    klass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    chipKlass: "bg-emerald-500/90 text-white",
  },
  ponto_atencao: {
    label: "Atenção",
    icon: AlertTriangle,
    klass: "bg-amber-50 text-amber-800 border-amber-200",
    chipKlass: "bg-amber-500/90 text-white",
  },
  resposta_tecnica: {
    label: "Resposta técnica",
    icon: Brain,
    klass: "bg-violet-50 text-violet-700 border-violet-200",
    chipKlass: "bg-violet-600/90 text-white",
  },
  soft_skill: {
    label: "Soft skill",
    icon: Zap,
    klass: "bg-sky-50 text-sky-700 border-sky-200",
    chipKlass: "bg-sky-500/90 text-white",
  },
  pergunta_chave: {
    label: "Pergunta-chave",
    icon: HelpCircle,
    klass: "bg-indigo-50 text-indigo-700 border-indigo-200",
    chipKlass: "bg-indigo-500/90 text-white",
  },
  outro: {
    label: "Momento",
    icon: PlayCircle,
    klass: "bg-slate-50 text-slate-700 border-slate-200",
    chipKlass: "bg-slate-600/90 text-white",
  },
};

const fmtClock = (s) => {
  const sec = Math.max(0, Math.round(Number(s) || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return `${m}:${String(ss).padStart(2, "0")}`;
};

const makeWaShareUrl = (phone, text) => {
  const clean = String(phone || "").replace(/[^\d+]/g, "");
  const normalized = clean.startsWith("+") ? clean.slice(1) : clean;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(text)}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const ACCESS_EVENT_LABELS = {
  room_provisioned: "abriu o painel de vídeo",
  join_url_requested: "clicou no link de entrar",
  confirm_page_viewed: "abriu a página de confirmação",
};

const AccessLogPanel = ({ interviewId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!interviewId) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/video/interviews/${interviewId}/access-log`,
        );
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [interviewId]);

  if (loading) return null;
  if (!data) return null;
  const events = data.events || [];
  if (events.length === 0) return null;

  const candidateLast = data.candidate_last_access || "";
  const recruiterLast = data.recruiter_last_access || "";
  const candidateAccessed = !!candidateLast;

  return (
    <div
      data-testid={`access-log-${interviewId}`}
      className="bg-white border border-slate-200 rounded-lg p-3 space-y-2"
    >
      <div className="flex items-center gap-2">
        <div
          className={`w-2 h-2 rounded-full ${candidateAccessed ? "bg-emerald-500" : "bg-slate-300"}`}
        />
        <h4 className="text-sm font-semibold text-slate-900">
          Atividade do link
        </h4>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`access-log-toggle-${interviewId}`}
          className="ml-auto text-[11px] text-brand-blue hover:underline inline-flex items-center gap-0.5"
        >
          {expanded ? "Ocultar" : `Ver ${events.length} eventos`}
          {expanded ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
        </button>
      </div>

      {/* Summary lines (always visible) */}
      <div className="space-y-1 text-xs">
        {candidateAccessed ? (
          <div className="flex items-center gap-1.5 text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>
              Candidato abriu o link
              <span className="text-slate-500 font-normal">
                {" "}· último acesso {fmtDate(candidateLast)}
              </span>
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-slate-500 italic">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>O candidato ainda NÃO abriu o link.</span>
          </div>
        )}
        {recruiterLast && (
          <div className="flex items-center gap-1.5 text-blue-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>
              Você visualizou o painel
              <span className="text-slate-500 font-normal">
                {" "}· último acesso {fmtDate(recruiterLast)}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Full timeline (collapsible) */}
      {expanded && (
        <ol
          data-testid={`access-log-list-${interviewId}`}
          className="border-t border-slate-100 pt-2 space-y-1.5 text-[11px]"
        >
          {events.map((e, idx) => (
            <li
              key={`${e.ts}-${idx}`}
              className="flex items-start gap-2 text-slate-700"
            >
              <span
                className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                  e.role === "candidate" ? "bg-emerald-500" : "bg-blue-500"
                }`}
              />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-slate-900">
                  {e.role === "candidate" ? "Candidato" : "Recrutador"}
                </span>{" "}
                {ACCESS_EVENT_LABELS[e.event] || e.event}
                <span className="text-slate-400">
                  {" · "}
                  {e.user_agent}
                  {e.ip_prefix && ` · ${e.ip_prefix}`}
                </span>
              </div>
              <time className="shrink-0 text-slate-500 tabular-nums">
                {fmtDate(e.ts)}
              </time>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};


const HighlightsPanel = ({
  interviewId,
  hasTranscript,
  onSeek,
  highlights,
  setHighlights,
}) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [generatedAt, setGeneratedAt] = useState("");

  // Fetch cached highlights on mount/expand
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!interviewId) return;
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/interviews/${interviewId}/highlights`);
        if (cancelled) return;
        setHighlights(res.data?.highlights || []);
        setGeneratedAt(res.data?.generated_at || "");
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId]);

  const generate = async (force = false) => {
    setGenerating(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/interviews/${interviewId}/highlights${force ? "?force=true" : ""}`,
        {},
      );
      setHighlights(res.data?.highlights || []);
      setGeneratedAt(res.data?.generated_at || "");
      if ((res.data?.highlights || []).length === 0) {
        toast.info(
          "A Maya não encontrou momentos destacáveis nessa entrevista.",
        );
      } else {
        toast.success(
          `Maya identificou ${res.data.highlights.length} momento${res.data.highlights.length > 1 ? "s" : ""}-chave.`,
        );
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao gerar destaques.";
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  // No transcript yet = nothing to analyse. Hide entirely.
  if (!hasTranscript) return null;

  return (
    <div
      data-testid={`highlights-panel-${interviewId}`}
      className="bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border border-violet-200 rounded-lg p-3"
    >
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shrink-0">
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 leading-tight">
              Destaques da Maya
            </h4>
            <p className="text-[10px] text-slate-500 leading-tight">
              {generatedAt
                ? `Atualizado ${new Date(generatedAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`
                : "Momentos-chave identificados pela IA"}
            </p>
          </div>
        </div>
        {!loading && (
          <button
            type="button"
            onClick={() => generate(highlights.length > 0)}
            disabled={generating}
            data-testid={`generate-highlights-${interviewId}`}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white inline-flex items-center gap-1 disabled:opacity-60 shrink-0"
          >
            {generating ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Sparkles className="w-3 h-3" />
            )}
            {highlights.length > 0 ? "Regenerar" : "Gerar destaques"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando destaques…
        </div>
      ) : error ? (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
          {error}
        </div>
      ) : highlights.length === 0 ? (
        <div className="text-xs text-slate-600 italic bg-white/60 rounded p-3 leading-relaxed">
          Ainda não foi gerado um highlight reel para essa entrevista. Clique em{" "}
          <strong>Gerar destaques</strong> e a Maya vai analisar a transcrição
          pra identificar os momentos mais relevantes.
        </div>
      ) : (
        <ol className="space-y-2">
          {highlights.map((h, idx) => {
            const meta =
              HIGHLIGHT_CATEGORIES[h.category] || HIGHLIGHT_CATEGORIES.outro;
            const CatIcon = meta.icon;
            const hasTimestamp = (h.timestamp_seconds || 0) > 0;
            return (
              <li
                key={`${h.timestamp_seconds}-${idx}`}
                data-testid={`highlight-${interviewId}-${idx}`}
                className={`bg-white border ${meta.klass} rounded-md overflow-hidden`}
              >
                <button
                  type="button"
                  disabled={!hasTimestamp}
                  onClick={() => hasTimestamp && onSeek && onSeek(h.timestamp_seconds)}
                  className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 ${
                    hasTimestamp ? "hover:bg-violet-50/50 cursor-pointer" : "cursor-default"
                  } transition-colors`}
                  title={hasTimestamp ? "Pular para esse momento no vídeo" : ""}
                >
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <span
                      className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded ${meta.chipKlass}`}
                    >
                      {hasTimestamp ? fmtClock(h.timestamp_seconds) : "—"}
                    </span>
                    <CatIcon className="w-4 h-4 opacity-75" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className="text-sm font-semibold text-slate-900">
                        {h.label}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider opacity-70 font-bold">
                        {meta.label}
                      </span>
                      <span className="ml-auto flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <span
                            key={i}
                            className={`w-1 h-1 rounded-full ${
                              i < (h.importance || 3)
                                ? "bg-violet-500"
                                : "bg-slate-200"
                            }`}
                          />
                        ))}
                      </span>
                    </div>
                    {h.quote && (
                      <blockquote className="text-xs text-slate-700 italic leading-snug border-l-2 border-current/30 pl-2 my-1">
                        “{h.quote}”
                      </blockquote>
                    )}
                    {h.reasoning && (
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {h.reasoning}
                      </p>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

const SummaryCard = ({ summary }) => {
  if (!summary) return null;
  const reco = SUMMARY_RECO_STYLES[summary.recomendacao] || SUMMARY_RECO_STYLES.considerar;
  const RecoIcon = reco.icon;
  return (
    <div
      data-testid="interview-summary-card"
      className="bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-lg p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-violet-600" />
        <h4 className="text-sm font-semibold text-slate-900">
          Resumo da IA
        </h4>
        <span
          className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${reco.klass}`}
        >
          <RecoIcon className="w-3 h-3" />
          {reco.label}
        </span>
      </div>
      {summary.resumo && (
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
          {summary.resumo}
        </p>
      )}
      {summary.pontos_fortes?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
            Pontos fortes
          </div>
          <ul className="space-y-1 text-sm text-slate-700">
            {summary.pontos_fortes.map((p, i) => (
              <li key={`pf-${p.slice(0, 30)}-${i}`} className="pl-3 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1 before:h-1 before:rounded-full before:bg-emerald-500">
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}
      {summary.pontos_atencao?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
            Pontos de atenção
          </div>
          <ul className="space-y-1 text-sm text-slate-700">
            {summary.pontos_atencao.map((p, i) => (
              <li key={`pa-${p.slice(0, 30)}-${i}`} className="pl-3 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1 before:h-1 before:rounded-full before:bg-amber-500">
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}
      {summary.justificativa_recomendacao && (
        <div className="text-xs text-slate-500 italic pt-1 border-t border-violet-100">
          {summary.justificativa_recomendacao}
        </div>
      )}
    </div>
  );
};

const InterviewBlock = ({ interview, candidate, onLoadRecordings }) => {
  const [expanded, setExpanded] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [loadingRec, setLoadingRec] = useState(false);
  const [recErr, setRecErr] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const [room, setRoom] = useState(null);
  const [loadingRoom, setLoadingRoom] = useState(false);
  const [roomErr, setRoomErr] = useState("");
  const [diagnosis, setDiagnosis] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [sendingWa, setSendingWa] = useState(false);
  const videoRef = useRef(null);

  const seekVideoTo = (seconds) => {
    const v = videoRef.current;
    if (!v) {
      toast.warning("Vídeo ainda não carregou. Aguarde alguns segundos.");
      return;
    }
    try {
      v.currentTime = Math.max(0, Number(seconds) || 0);
      // Try to play — modern browsers throw if user hasn't interacted yet.
      const playPromise = v.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Autoplay blocked — that's fine, user can hit play manually
        });
      }
      // Scroll the video into view
      v.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (err) {
      console.error("Failed to seek video:", err);
    }
  };

  const handleExpand = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    await refreshRecordings();
    // Fetch room links if interview is upcoming/confirmed (idempotent — same
    // links for every call so this just retrieves the existing room).
    if (interview.status === "confirmed" && !room && !loadingRoom) {
      setLoadingRoom(true);
      setRoomErr("");
      try {
        const res = await axios.post(
          `${API}/video/interviews/${interview.id}/room`,
          {},
        );
        setRoom(res.data);
      } catch (err) {
        setRoomErr(
          err?.response?.data?.detail || "Não foi possível recuperar os links.",
        );
      } finally {
        setLoadingRoom(false);
      }
    }
  };

  // Always call the backend to list recordings — `recording_count` from the
  // DB depends on the 100ms webhook, which may not have arrived yet (e.g.
  // recording is still processing, or webhook is not configured in this
  // environment). We hit the live recordings API as the source of truth.
  const refreshRecordings = async () => {
    // Skip only if no room was ever provisioned — a user may have joined the
    // room before the scheduled starts_at (e.g. early test session), so we
    // cannot rely on a time-window check here.
    if (!(interview.hms_room_id || interview.hms_room_name)) return;
    setLoadingRec(true);
    setRecErr("");
    try {
      const list = await onLoadRecordings(interview.id);
      setRecordings(list);
    } catch (err) {
      setRecErr(err?.response?.data?.detail || "Erro ao buscar gravações");
    } finally {
      setLoadingRec(false);
    }
  };

  const runDiagnosis = async () => {
    setLoadingDiag(true);
    try {
      const res = await axios.get(`${API}/video/interviews/${interview.id}/debug`);
      setDiagnosis(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao executar diagnóstico");
    } finally {
      setLoadingDiag(false);
    }
  };

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copiado!`))
      .catch(() => toast.error("Não consegui copiar."));
  };

  const aiReady = !!interview.ai_summary && Object.keys(interview.ai_summary).length > 0;
  const hasRecordings = (interview.recording_count || 0) > 0;
  const hasTranscriptOnly = !!interview.transcript && !aiReady;
  // True when the interview happened (or will happen in the next 30min).
  const isPastOrSoon =
    new Date(interview.starts_at).getTime() - Date.now() < 1000 * 60 * 30;
  // A room was provisioned if we have the 100ms room ID — the recording may
  // exist even if starts_at is still in the future (e.g. the user joined early
  // for a test run), so we always show the recordings section for provisioned
  // rooms that aren't cancelled.
  const roomProvisioned =
    !!(interview.hms_room_id || interview.hms_room_name) &&
    interview.status !== "cancelled";
  const showRecordingsSection =
    hasRecordings ||
    recordings.length > 0 ||
    loadingRec ||
    !!recErr ||
    roomProvisioned ||
    (isPastOrSoon && interview.status !== "cancelled");

  return (
    <div
      data-testid={`interview-block-${interview.id}`}
      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
    >
      <button
        type="button"
        onClick={handleExpand}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
      >
        <div
          className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
            hasRecordings ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"
          }`}
        >
          <VideoIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">
              {fmtDate(interview.starts_at)}
            </span>
            {interview.status === "cancelled" && (
              <span className="px-1.5 py-0.5 text-[10px] uppercase font-bold tracking-wider bg-slate-100 text-slate-500 rounded">
                cancelada
              </span>
            )}
            {hasRecordings && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <VideoIcon className="w-2.5 h-2.5" />
                {interview.recording_count} gravação{interview.recording_count > 1 ? "ões" : ""}
              </span>
            )}
            {aiReady && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-50 text-violet-700 border border-violet-200">
                <Sparkles className="w-2.5 h-2.5" /> IA pronta
              </span>
            )}
            {hasTranscriptOnly && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                <FileText className="w-2.5 h-2.5" /> Transcrita
              </span>
            )}
            {hasRecordings && !interview.transcript && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                <Clock className="w-2.5 h-2.5" /> Processando…
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Gestor: {interview.manager_name || "—"}
          </div>
        </div>
        <div className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500">
          {expanded ? (
            <>
              Fechar
              <ChevronUp className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              {interview.status === "confirmed" ? "Ver links" : "Detalhes"}
              <ChevronDown className="w-3.5 h-3.5" />
            </>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 bg-slate-50/40 border-t border-slate-200">
          {/* Access links (only for confirmed/upcoming) */}
          {interview.status === "confirmed" && (
            <div data-testid={`access-links-${interview.id}`}>
              <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-1.5 flex items-center gap-1">
                <Link2 className="w-3 h-3" />
                Links de acesso
              </div>
              {loadingRoom ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Preparando links...
                </div>
              ) : roomErr ? (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  {roomErr}
                </div>
              ) : room ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 min-w-0">
                    <Link2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">
                        Gestor (você)
                      </div>
                      <div className="text-[11px] text-slate-700 font-mono truncate">
                        {room.host_join_url}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => copy(room.host_join_url, "Link do gestor")}
                      data-testid={`copy-host-${interview.id}`}
                      className="text-[11px] text-brand-blue hover:underline shrink-0 inline-flex items-center gap-1 px-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copiar
                    </button>
                  </div>
                  <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 min-w-0">
                    <Link2 className="w-3.5 h-3.5 text-violet-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] uppercase tracking-wider font-bold text-violet-700">
                        Candidato (envie este)
                      </div>
                      <div className="text-[11px] text-violet-900 font-mono truncate">
                        {room.candidate_join_url}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        copy(room.candidate_join_url, "Link do candidato")
                      }
                      data-testid={`copy-candidate-${interview.id}`}
                      className="text-[11px] text-violet-700 hover:underline shrink-0 inline-flex items-center gap-1 px-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copiar
                    </button>
                  </div>
                  {candidate?.phone && (
                    <button
                      type="button"
                      data-testid={`send-wa-${interview.id}`}
                      disabled={sendingWa}
                      onClick={async () => {
                        setSendingWa(true);
                        const waMsg =
                          `Olá ${candidate.name || "candidato"}! 👋\n\n` +
                          `Sua entrevista foi agendada para ${fmtDate(interview.starts_at)}.\n\n` +
                          `Acesse pelo link: ${room.candidate_join_url}\n\n` +
                          `Nos vemos lá! 🎥`;
                        try {
                          await axios.post(`${API}/notifications/whatsapp`, {
                            candidate_id: candidate.id,
                            message: waMsg,
                          });
                          toast.success("Link da entrevista enviado por WhatsApp!");
                        } catch (err) {
                          const detail = err?.response?.data?.detail;
                          const errorMsg =
                            Array.isArray(detail)
                              ? detail.map((e) => e?.msg || JSON.stringify(e)).join("; ")
                              : typeof detail === "string"
                              ? detail
                              : "Erro ao enviar WhatsApp";
                          toast.error(errorMsg);
                        } finally {
                          setSendingWa(false);
                        }
                      }}
                      className="flex items-center justify-center gap-1.5 w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold py-2 px-3 rounded-md transition-colors text-xs"
                    >
                      {sendingWa ? (
                        <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5 shrink-0" />
                      )}
                      <span className="truncate">
                        {sendingWa
                          ? "Enviando…"
                          : `Enviar link por WhatsApp para ${candidate.name?.split(" ")[0] || "candidato"}`}
                      </span>
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* AI summary */}
          <SummaryCard summary={interview.ai_summary} />

          {/* Access log — who opened the link, when */}
          <AccessLogPanel interviewId={interview.id} />

          {/* Player — show if we got recordings from the API regardless of webhook state */}
          {showRecordingsSection && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-1.5 flex items-center gap-2">
                <span>Gravação</span>
                <button
                  type="button"
                  onClick={refreshRecordings}
                  disabled={loadingRec}
                  data-testid={`refresh-recordings-${interview.id}`}
                  className="ml-auto text-[10px] text-brand-blue hover:underline inline-flex items-center gap-1 normal-case tracking-normal disabled:opacity-50"
                  title="Buscar de novo no 100ms (útil se o webhook ainda não chegou)"
                >
                  <RefreshCw className={`w-3 h-3 ${loadingRec ? "animate-spin" : ""}`} />
                  Atualizar
                </button>
              </div>
              {loadingRec ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" /> Carregando link da gravação…
                </div>
              ) : recErr ? (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
                  {recErr}
                </div>
              ) : recordings.length === 0 ? (
                <div className="space-y-2">
                  <div className="text-xs text-slate-500 italic">
                    Nenhuma gravação encontrada no 100ms ainda. Após o fim da entrevista, o processamento pode levar 5–10 minutos. Clique em <strong>Atualizar</strong> pra tentar de novo.
                  </div>
                  <button
                    type="button"
                    onClick={runDiagnosis}
                    disabled={loadingDiag}
                    data-testid={`diagnose-${interview.id}`}
                    className="text-[10px] text-violet-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                    title="Consulta o 100ms diretamente pra mostrar sessões, jobs de gravação e a causa provável"
                  >
                    {loadingDiag ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <AlertCircle className="w-3 h-3" />
                    )}
                    Diagnosticar no 100ms
                  </button>
                  {diagnosis && (
                    <div
                      data-testid={`diagnosis-result-${interview.id}`}
                      className="bg-violet-50 border border-violet-200 rounded-md p-3 text-[11px] space-y-2"
                    >
                      <div className="font-semibold text-violet-900">
                        Diagnóstico
                      </div>
                      {diagnosis.summary?.message && (
                        <div className="text-slate-800 leading-relaxed">
                          {diagnosis.summary.message}
                        </div>
                      )}
                      {diagnosis.summary?.error && (
                        <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                          {diagnosis.summary.error}
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2 pt-1 text-center">
                        <div className="bg-white rounded border border-slate-200 px-2 py-1.5">
                          <div className="text-base font-bold text-slate-900">
                            {(diagnosis.hms?.sessions || []).length}
                          </div>
                          <div className="text-[9px] uppercase tracking-wider text-slate-500">
                            sessões
                          </div>
                        </div>
                        <div className="bg-white rounded border border-slate-200 px-2 py-1.5">
                          <div className="text-base font-bold text-slate-900">
                            {(diagnosis.hms?.recording_jobs || []).length}
                          </div>
                          <div className="text-[9px] uppercase tracking-wider text-slate-500">
                            jobs gravação
                          </div>
                        </div>
                        <div className="bg-white rounded border border-slate-200 px-2 py-1.5">
                          <div className="text-base font-bold text-slate-900">
                            {(diagnosis.hms?.recording_assets || []).length}
                          </div>
                          <div className="text-[9px] uppercase tracking-wider text-slate-500">
                            assets
                          </div>
                        </div>
                      </div>
                      {(diagnosis.hms?.recording_jobs || []).length > 0 && (
                        <details className="text-[10px] text-slate-600">
                          <summary className="cursor-pointer hover:text-slate-900">
                            Ver detalhes dos jobs ({diagnosis.hms.recording_jobs.length})
                          </summary>
                          <pre className="mt-1 bg-white border border-slate-200 rounded p-2 overflow-x-auto max-h-40 text-[10px]">
                            {JSON.stringify(
                              diagnosis.hms.recording_jobs.map((j) => ({
                                id: j.id,
                                status: j.status,
                                session_id: j.session_id,
                                started_at: j.started_at,
                                stopped_at: j.stopped_at,
                                asset_types: j.asset_types,
                              })),
                              null,
                              2,
                            )}
                          </pre>
                        </details>
                      )}
                      {(diagnosis.hms?.errors || []).length > 0 && (
                        <details className="text-[10px] text-rose-700">
                          <summary className="cursor-pointer">Erros ({diagnosis.hms.errors.length})</summary>
                          <ul className="mt-1 list-disc pl-4">
                            {diagnosis.hms.errors.map((e, i) => (
                              <li key={i}>{e}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {recordings.map((r, idx) => {
                    // The proxy endpoint requires auth; the <video> element
                    // cannot set headers, so we pass the JWT as a query param.
                    const authToken = localStorage.getItem("rh_token") || "";
                    const videoSrc = authToken
                      ? `${r.url}?token=${encodeURIComponent(authToken)}`
                      : r.url;
                    return (
                      <div
                        key={r.asset_id}
                        data-testid={`recording-${r.asset_id}`}
                        className="bg-black rounded-md overflow-hidden"
                      >
                        <video
                          ref={idx === 0 ? videoRef : undefined}
                          src={videoSrc}
                          controls
                          preload="metadata"
                          className="w-full max-h-[420px]"
                        >
                          Seu navegador não suporta vídeo HTML5.
                        </video>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Maya AI Highlight Reel — clickable timestamps */}
          {interview.transcript && (
            <HighlightsPanel
              interviewId={interview.id}
              hasTranscript={!!interview.transcript}
              onSeek={seekVideoTo}
              highlights={highlights}
              setHighlights={setHighlights}
            />
          )}

          {/* Transcript (collapsible) */}
          {interview.transcript && (
            <div>
              <button
                type="button"
                onClick={() => setShowTranscript((v) => !v)}
                data-testid={`toggle-transcript-${interview.id}`}
                className="text-xs text-brand-blue hover:underline inline-flex items-center gap-1"
              >
                <FileText className="w-3 h-3" />
                {showTranscript ? "Ocultar" : "Ver"} transcrição completa
                <span className="text-slate-400 font-normal">
                  ({Math.round(interview.transcript.length / 1000)}k caracteres)
                </span>
              </button>
              {showTranscript && (
                <pre className="mt-2 bg-white border border-slate-200 rounded p-3 text-[12px] text-slate-700 whitespace-pre-wrap font-sans leading-relaxed max-h-[300px] overflow-y-auto">
                  {interview.transcript}
                </pre>
              )}
            </div>
          )}

          {!hasRecordings && !isPastOrSoon && interview.status !== "cancelled" && (
            <div className="text-xs text-slate-500 italic">
              Esta entrevista ainda não foi realizada ou não foi gravada.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CandidateInterviewsPanel = ({ candidateId, candidate }) => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/candidates/${candidateId}/interviews`);
      setInterviews(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao carregar entrevistas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const loadRecordings = async (interviewId) => {
    const res = await axios.get(`${API}/video/interviews/${interviewId}/recordings`);
    return res.data?.recordings || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando entrevistas…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-4">
        {error}
      </div>
    );
  }

  if (interviews.length === 0) {
    return (
      <div className="text-center py-10 text-sm text-slate-500" data-testid="no-interviews">
        Nenhuma entrevista agendada ainda.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="candidate-interviews-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Clique em uma entrevista para ver gravação, transcrição e resumo da IA.
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          className="text-xs h-7"
          data-testid="refresh-interviews-btn"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> Atualizar
        </Button>
      </div>
      {interviews.map((iv) => (
        <InterviewBlock
          key={iv.id}
          interview={iv}
          candidate={candidate}
          onLoadRecordings={loadRecordings}
        />
      ))}
    </div>
  );
};

export default CandidateInterviewsPanel;
